import type { ReactNode } from "react";

// Reusable table chrome for the wallboard's searchable/sortable/paginated tables
// (feedback #6): a search box, a sortable header cell, and a pager that always
// shows "X–Y of Z" so the operator knows the full size of the data — never a
// silent slice.

export function SearchInput({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) {
  return (
    <input
      type="search"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid="table-search"
      className="w-64 rounded-lg border border-edge bg-surface px-3 py-1.5 text-sm text-primary placeholder:text-muted focus:border-accent focus:outline-none"
    />
  );
}

export function Th({
  label,
  field,
  sort,
  order,
  onSort,
  align = "left",
  className = "",
  hint,
}: {
  label: string;
  field?: string;
  sort?: string;
  order?: "asc" | "desc";
  onSort?: (field: string) => void;
  align?: "left" | "right";
  className?: string;
  hint?: string;
}) {
  const sortable = !!field && !!onSort;
  const active = sortable && sort === field;
  return (
    <th
      onClick={sortable ? () => onSort!(field!) : undefined}
      data-testid={field ? `th-${field}` : undefined}
      title={hint}
      className={[
        "px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted",
        align === "right" ? "text-right" : "text-left",
        sortable ? "cursor-pointer select-none hover:text-secondary" : "",
        className,
      ].join(" ")}
    >
      {label}
      {active ? <span className="ml-1 text-accent">{order === "asc" ? "↑" : "↓"}</span> : null}
    </th>
  );
}

export function Pager({
  page,
  pageSize,
  total,
  onPage,
}: {
  page: number;
  pageSize: number;
  total: number;
  onPage: (p: number) => void;
}) {
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  return (
    <div className="flex items-center justify-between px-1 py-2 text-xs text-muted" data-testid="pager">
      <span data-testid="pager-summary">
        Showing {from}–{to} of {total}
      </span>
      <div className="flex items-center gap-1">
        <PagerButton disabled={page <= 1} onClick={() => onPage(page - 1)}>
          Prev
        </PagerButton>
        <span className="px-1">
          {page} / {lastPage}
        </span>
        <PagerButton disabled={page >= lastPage} onClick={() => onPage(page + 1)}>
          Next
        </PagerButton>
      </div>
    </div>
  );
}

function PagerButton({
  disabled,
  onClick,
  children,
}: {
  disabled: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      disabled={disabled}
      onClick={onClick}
      className="rounded-md border border-edge px-2 py-1 text-secondary transition-colors hover:bg-card-hover disabled:cursor-not-allowed disabled:opacity-40"
    >
      {children}
    </button>
  );
}
